import { useAtomValue } from "jotai";
import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { useCurrentPageType } from "shared/hooks";
import { PageType } from "shared/lib/page-type";
import { notEmpty } from "shared/lib/utils";

import { getArticleType } from "scmp-app/components/article/article-render/helpers";
import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { DataMap } from "scmp-app/components/plus/plus-article-type-title/consts";
import { PlusWidgetTheme } from "scmp-app/components/plus/plus-widget";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import type { plusWidgetHighlight$key } from "scmp-app/queries/__generated__/plusWidgetHighlight.graphql";

import {
  Container,
  CoverLink,
  Headline,
  HeadlineLink,
  LabelContainer,
  StyledPlusArticleTypeLink,
  TypeOrSectionLabel,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  plusWidgetTheme: PlusWidgetTheme;
  reference: plusWidgetHighlight$key;
  withArticleType?: boolean;
};

export const ContentItemPlusHighlight: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  plusWidgetTheme = PlusWidgetTheme.DARK,
  ref,
  reference,
  withArticleType = true,
}) => {
  const entity = useFragment(
    graphql`
      fragment plusWidgetHighlight on Content {
        __typename
        entityId
        urlAlias
        ...entityLink
        ...helpersCheckIsPlusArticle
        ...helpersGetArticleTypeArticle
        ...hooksContentItemProvidersContent
          @arguments(
            withHeadline: true
            withCoverImage: true
            withCoverImageSize540x360: true
            withSections: true
          )
        ...plusArticleTypeTitleContent
      }
    `,
    reference,
  );

  const providers = useContentItemProviders(entity);
  const articleTypeId = getArticleType(entity) ?? "";
  const { title, type } = DataMap[articleTypeId] ?? {};
  const isPlusArticle = checkIsPlusArticle(entity);
  const currentPageType = useCurrentPageType();
  const pageType = currentPageType === PageType.Homepage ? "homepage" : "section";

  const handleRenderArticleTypeOrSections = () => {
    if (isPlusArticle || type === "infographic") {
      return type === "infographic" ? (
        <TypeOrSectionLabel>{title}</TypeOrSectionLabel>
      ) : (
        withArticleType && notEmpty(title) && (
          <StyledPlusArticleTypeLink
            pathname={`/plus?plus-detail=${type}&module=content_discovery&pgtype=${pageType}`}
          >
            {title}
          </StyledPlusArticleTypeLink>
        )
      );
    }
    return <TypeOrSectionLabel>{providers.sections({ isDisableLink: true })}</TypeOrSectionLabel>;
  };

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const hasScmpPlusAccessRight = asyncRosettaState?.result?.hasScmpPlusAccessRight ?? false;
  const handleItemClickEvent = () => {
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: entity?.entityId ?? "",
        block_section: "highlight_from_plus",
        destination_url: entity?.urlAlias ?? "",
        page_type: pageType,
        widget_name: "content_discovery",
        widget_version: hasScmpPlusAccessRight ? "subscriber" : "non_subscriber",
      },
      subcategory: "widget",
    });
  };

  return (
    <Container $plusWidgetTheme={plusWidgetTheme} className={className} ref={ref}>
      <LabelContainer>{handleRenderArticleTypeOrSections()}</LabelContainer>
      <HeadlineLink
        onClick={handleItemClickEvent}
        query={{ display: "plus", module: "content_discovery", pgtype: pageType }}
        reference={entity}
      >
        <Headline>
          {providers.headline({
            preferSocialHeadline: true,
          })}
        </Headline>
      </HeadlineLink>
      <CoverLink
        onClick={handleItemClickEvent}
        query={{ display: "plus", module: "content_discovery", pgtype: pageType }}
        reference={entity}
      >
        {providers.coverImage({
          responsiveVariants: {
            desktopUp: "size540x360",
            mobileUp: "size540x360",
            tabletUp: "size540x360",
          },
        })}
      </CoverLink>
    </Container>
  );
};

ContentItemPlusHighlight.displayName = "ContentItemPlusHighlight";
